import { useRouter } from "next/router";
import type { NextRouter } from "next/router";
import type { UrlObject } from "url";

type Url = string | UrlObject;
type TransitionOptions = Parameters<NextRouter["replace"]>[2];

type NextRouterExtended = NextRouter & {
  pushShallow: (url: Url) => Promise<boolean>;
  replaceShallow: (url: Url) => Promise<boolean>;
  pushSchema: (url: Partial<UrlObject> | string) => Promise<boolean>;
  replaceSchema: (...a: Parameters<NextRouter["replace"]>) => Promise<boolean>;
};

export const useRouterExtended = () => {
  const router = useRouter() as NextRouterExtended;

  // shallow push method to next router
  router.pushShallow = async function (url: Url) {
    return this.push(url, undefined, { shallow: true });
  };

  // shallow replace method to next router
  router.replaceShallow = async function (url: Url) {
    return this.replace(url, undefined, { shallow: true });
  };

  router.replaceSchema = async function (url, as, options) {
    const teamSchema = router?.query?.teamSchema as string;
    if (typeof url == "string") {
      const query = {} as any;
      if (teamSchema) query.teamSchema = teamSchema;
      return this.replace({ pathname: url, query }, as, options);
    } else {
      const query = (url?.query || {}) as Record<string, string>;
      if (teamSchema) query.teamSchema = teamSchema;
      return this.replace({ ...url, query }, as, options);
    }
  };

  // push method with teamSchema query param always
  router.pushSchema = function (url) {
    const teamSchema = router.query.teamSchema as string;

    const errorMessage =
      "Using pushWithSchema without teamSchema query param available in the URL, make sure you are using this method where teamSchema is available in the url path.";
    if (!teamSchema) throw new Error(errorMessage);

    if (typeof url == "string") {
      return this.push({ pathname: url, query: { teamSchema } });
    }
    return this.push({
      ...url,
      pathname: url?.pathname,
      query: { ...(url?.query as Record<string, string> | undefined), teamSchema },
    });
  };

  return router;
};
